import { Text, Tooltip } from "@mantine/core"
import { createColumnHelper } from "@tanstack/react-table"
import { ActionIcon, Loader, Select } from "@tfg/ui"
import { useMemo, useState } from "react"

import { AdminResponses } from "@tfg/types/endpoints/admin"

import { queryClient } from "~/backend/queryClient"
import { useFindOperationChainsQuery } from "~/pages/Admin/Ingestion/OpChains/hooks/useFindOperationChainsQuery"
import { useGetEcobalyseProductTypesQuery } from "~/pages/Admin/MasterLists/ProductTypes/hooks/useGetEcobalyseProductTypesQuery"

import { EditableCellWithTextInput } from "../../Translations/columns"
import { useUpdateTranslationsMutation } from "../../Translations/hooks"

import {
  useDeleteProductTypeMutation,
  useUpdateProductTypeMutation,
} from "./hooks"

export const useColumns = () => {
  const columnHelper = createColumnHelper<AdminResponses.ProductTypes>()
  const { mutate: updateTranslation } = useUpdateTranslationsMutation({
    onSuccess: async () => {
      await queryClient.invalidateQueries(["productTypes"])
    },
  })

  const [productId, setProductId] = useState<number | null>(null)
  const { mutate: updateProductType, isLoading: isProductUpdating } =
    useUpdateProductTypeMutation({
      onSuccess: () => {
        setProductId(null)
      },
    })
  const { mutate: deleteProductType } = useDeleteProductTypeMutation()

  const { data: ecobalyseProductTypes, isLoading: isLoadingProductTypes } =
    useGetEcobalyseProductTypesQuery()

  const { data: productOpChains } = useFindOperationChainsQuery({
    type: "PRODUCT",
    showDeleted: false,
  })

  return useMemo(
    () => [
      columnHelper.accessor("id", {
        header: "ID",
        cell: ({ getValue }) => <Text>{getValue()}</Text>,
      }),
      columnHelper.accessor("name", {
        header: "Key",
        cell: ({ getValue }) => <Text>{getValue()}</Text>,
      }),
      columnHelper.accessor("operationChain", {
        header: "Product Operation Chain",
        enableSorting: false,
        cell: ({ row: { original: productType }, getValue }) => {
          return (
            <Tooltip
              multiline
              maw={300}
              label="Changing this will have an impact on existing information requests, please contact tech team to change this."
            >
              <Select
                defaultValue={getValue().id}
                miw={150}
                data={productOpChains?.data?.map((chain) => ({
                  label: chain.name,
                  value: chain.id,
                }))}
                disabled={true}
                onChange={(newValue) => {
                  if (newValue === null) return
                  updateProductType({
                    productId: productType.id,
                    productType: {
                      name: productType.name,
                      ecobalyseId: productType.ecobalyseId,
                      operationChainId: newValue,
                    },
                  })
                  setProductId(productType.id)
                }}
              />
            </Tooltip>
          )
        },
      }),
      columnHelper.accessor("ecobalyseId", {
        header: "Ecobalyse Id",
        enableSorting: false,
        cell: ({ row: { original: productType }, getValue }) => {
          return isLoadingProductTypes ? (
            <Loader type="dots" size={16} />
          ) : (
            <Select
              defaultValue={getValue()}
              key={productType.id}
              data={ecobalyseProductTypes?.map((ecobalyseProductType) => ({
                value: ecobalyseProductType.id,
                label: ecobalyseProductType.name,
              }))}
              onChange={(newValue) => {
                updateProductType({
                  productId: productType.id,
                  productType: {
                    ...productType,
                    operationChainId: productType.operationChain.id,
                    ecobalyseId: newValue,
                  },
                })
                setProductId(productType.id)
              }}
              isLoading={isProductUpdating && productId === productType.id}
            />
          )
        },
      }),
      columnHelper.accessor("EN", {
        header: "EN",
        enableSorting: false,
        cell: ({ row: { original: productType }, getValue }) => (
          <EditableCellWithTextInput
            defaultValue={getValue()}
            onSave={(newValue: string) => {
              updateTranslation({
                id: productType.textContentId,
                translatedLanguage: "EN",
                translation: newValue,
              })
            }}
          />
        ),
      }),
      columnHelper.accessor("FR", {
        header: "FR",
        enableSorting: false,
        cell: ({ row: { original: productType }, getValue }) => (
          <EditableCellWithTextInput
            defaultValue={getValue()}
            onSave={(newValue: string) => {
              updateTranslation({
                id: productType.textContentId,
                translatedLanguage: "FR",
                translation: newValue,
              })
            }}
          />
        ),
      }),
      columnHelper.accessor((row) => row.id, {
        header: () => null,
        id: "delete",
        enableSorting: false,
        cell: ({ row }) => {
          return (
            <ActionIcon
              type="delete"
              onClick={() => deleteProductType(row.original.id)}
            />
          )
        },
      }),
    ],
    [
      updateTranslation,
      deleteProductType,
      columnHelper,
      ecobalyseProductTypes,
      isLoadingProductTypes,
      isProductUpdating,
      productOpChains?.data,
      updateProductType,
      productId,
    ]
  )
}
